import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import type { FermentationGroupId, FermentationInvestigationId } from "@brewbase/db/types";
import { FermentationInvestigationStatusEnum } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { cn } from "@/lib/utils";
import { formatCalendarDate, isCalendarDatePast } from "@/lib/date";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PlusIcon } from "lucide-react";
import { ROOT_CAUSE_LABELS } from "@/features/fermentation-investigation/root-cause-labels";
import { IncidentBrowser } from "@/features/fermentation-investigation/incident-browser";

export const Route = createFileRoute("/fermentations/investigations/")({
  component: InvestigationsListPage,
  head: () => ({ meta: [{ title: "BrewBase - Investigations" }] }),
});

type Tab = "open" | "closed" | "all" | "incidents";

function InvestigationsListPage() {
  const [tab, setTab] = useState<Tab>("open");
  const status =
    tab === "open"
      ? FermentationInvestigationStatusEnum.OPEN
      : tab === "closed"
        ? FermentationInvestigationStatusEnum.CLOSED
        : undefined;
  const { data, isLoading } = trpc.fermentations.investigations.list.useQuery(
    { status },
    { enabled: tab !== "incidents" },
  );
  const { data: summary } = trpc.fermentations.investigations.summary.useQuery();

  const tabs: Array<{ id: Tab; label: string; count?: number }> = [
    { id: "open", label: "Open", count: summary?.open },
    { id: "closed", label: "Closed" },
    { id: "all", label: "All" },
    { id: "incidents", label: "Incidents" },
  ];

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-medium">Investigations</h1>
          <p className="text-xs text-muted-foreground">
            Root cause analyses and corrective / preventive actions across runs
          </p>
        </div>
        <div className="flex items-center gap-1">
          {tabs.map((t) => (
            <Button
              key={t.id}
              variant="ghost"
              size="sm"
              onClick={() => setTab(t.id)}
              className={cn(
                "h-7 px-2 text-xs",
                tab === t.id
                  ? "bg-primary/10 text-primary hover:bg-primary/15"
                  : "text-muted-foreground",
              )}
            >
              {t.label}
              {t.count != null && t.count > 0 && (
                <span className="ml-1.5 px-1 text-xs bg-olive-4 text-olive-11">{t.count}</span>
              )}
            </Button>
          ))}
          <div className="w-px h-5 bg-olive-6 mx-1" />
          <NewInvestigationButton />
        </div>
      </div>

      {tab === "incidents" ? (
        <IncidentBrowser />
      ) : isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (data?.length ?? 0) === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          {tab === "open" ? "No open investigations." : "Nothing here."}
        </div>
      ) : (
        <table className="w-full text-sm border border-olive-6">
          <thead>
            <tr className="text-xs text-olive-11 bg-olive-2 border-b border-olive-6">
              <th className="text-left font-medium px-2 py-1.5">Title</th>
              <th className="text-left font-medium px-2 py-1.5 w-20">Status</th>
              <th className="text-left font-medium px-2 py-1.5 w-48">Runs</th>
              <th className="text-left font-medium px-2 py-1.5 w-44">Root cause</th>
              <th className="text-left font-medium px-2 py-1.5 w-36">Preventive owner</th>
              <th className="text-left font-medium px-2 py-1.5 w-28">Due</th>
              <th className="text-left font-medium px-2 py-1.5 w-28">Updated</th>
            </tr>
          </thead>
          <tbody>
            {data!.map((inv) => {
              const isOpen = inv.status === FermentationInvestigationStatusEnum.OPEN;
              const overdue = isOpen && inv.preventive_due_on && isCalendarDatePast(inv.preventive_due_on);
              return (
                <tr key={inv.id} className="border-b border-olive-5 hover:bg-olive-3">
                  <td className="px-2 py-1.5">
                    <Link
                      to="/fermentations/investigations/$investigation_id"
                      params={{ investigation_id: inv.id as FermentationInvestigationId }}
                      className="font-medium hover:underline"
                    >
                      {inv.title}
                    </Link>
                    <span className="ml-2 text-xs text-olive-9">
                      {inv.incident_count} incident{Number(inv.incident_count) === 1 ? "" : "s"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge variant={isOpen ? "warning" : "default"}>{isOpen ? "Open" : "Closed"}</Badge>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {inv.groups.map((g) => (
                        <Link
                          key={g.id}
                          to="/fermentations/$group_id/results/analysis"
                          params={{ group_id: g.id as FermentationGroupId }}
                          className="text-xs bg-olive-3 border border-olive-5 px-1 hover:underline"
                        >
                          {g.name}
                        </Link>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    {inv.root_cause_category ? ROOT_CAUSE_LABELS[inv.root_cause_category] : (
                      <span className="text-olive-9">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{inv.preventive_responsible_name ?? <span className="text-olive-9">—</span>}</td>
                  <td className={cn("px-2 py-1.5", overdue && "text-tomato-11 font-medium")}>
                    {inv.preventive_due_on ? formatCalendarDate(inv.preventive_due_on) : <span className="text-olive-9">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-olive-11">{new Date(inv.updated_at).toLocaleDateString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** A blank investigation: title now, incidents linked later from the page. */
function NewInvestigationButton() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const create = trpc.fermentations.investigations.create.useMutation({
    onSuccess: (inv) => {
      utils.fermentations.investigations.invalidate();
      setOpen(false);
      navigate({
        to: "/fermentations/investigations/$investigation_id",
        params: { investigation_id: inv.id as FermentationInvestigationId },
      });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" className="h-7">
          <PlusIcon className="w-4 h-4 mr-1" />
          New investigation
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-3 space-y-2">
        <div className="text-xs text-olive-11">Title</div>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Recurring foam-out on CF tanks"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && title.trim()) create.mutate({ title: title.trim(), incidentIds: [] });
          }}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate({ title: title.trim(), incidentIds: [] })}
          >
            Create
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
